/**
 * Configuration slice
 *
 * Zustand slice for platform configuration state including pricing defaults.
 */

import { clearError as clearOpError, hasOperationLoaded, isOperationLoading } from '@gw/ui';

import { configApi } from '@/api/config';
import { createAsyncSliceAction } from '@/stores/helpers/asyncSliceAction';

import type {
  PricingConfig,
  ErrorStates,
  ErrorStatuses,
  LoadedTimestamps,
  LoadingStates,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

export interface ConfigSlice {
  // State
  pricingConfig: PricingConfig | null;
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;
  errorStatuses: ErrorStatuses;

  // Actions
  fetchPricingConfig: () => Promise<PricingConfig | null>;
  clearConfigError: (operation: string) => void;
}

// Default config for scalar platform values (split rates, limits).
// Used only as a fallback for admin UI components that display computed values.
// tokenPackages is intentionally empty — token packages must come from the
// live pricing API. Using stale hardcoded package IDs causes 400 errors on purchase.
export const DEFAULT_PRICING_CONFIG: PricingConfig = {
  platformMarkupRate: 0.35,
  tokenPrice: 0.1,
  minSessionDuration: 15,
  maxSessionDuration: 480,
  providerSplitPercentage: 65,
  platformSplitPercentage: 35,
  tokenPackages: [],
};

export const createConfigSlice: StateCreator<ConfigSlice, [], [], ConfigSlice> = (set, get) => ({
  pricingConfig: null,
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  errorStatuses: {},

  fetchPricingConfig: async () => {
    const operation = 'fetchPricingConfig';
    const currentState = get();

    // Guard: Don't fetch if already loading
    if (isOperationLoading(currentState.isLoading, operation)) {
      return currentState.pricingConfig;
    }

    // Guard: Return cached data if already loaded
    if (hasOperationLoaded(currentState.hasLoaded, operation) && currentState.pricingConfig) {
      return currentState.pricingConfig;
    }

    await createAsyncSliceAction<ConfigSlice, PricingConfig>(
      operation,
      () => configApi.getPricingConfig(),
      (config) => ({ pricingConfig: config }),
      set,
      get
    )();

    return get().pricingConfig;
  },

  clearConfigError: (operation: string) => {
    const state = get();
    set({
      errors: clearOpError(state.errors, operation),
    });
  },
});
